import moment from 'moment';
import _ from 'lodash';

import {
  ProductionRecordFieldEnum as ProdRecordFields,
  ProductionRecordDetailFieldEnum as ProdRecordDetailFields,
  ProductionRecipeLeftoverEnum as ProdRecipeLeftoverFields,
  ProductionRecipeLeftoverDetailEnum as ProdLeftoverDetailFields,
} from 'Common/FieldEnum';
import { loading, success, handleErrorException } from 'State/actions';
import {
  getTemperaturesInUse,
  getTemperaturesList,
  addUIFieldsToDetails,
  convertProdRecordForSave,
  getLeftoverChanges,
  getNewLeftoverAmount,
  getMaxAllowedForLeftover,
  createToggleId,
} from 'Pages/manageproduction/helpers/productionRecord';
import { NUMBER_OF_DAYS_TO_DISPLAY } from 'Pages/manageproduction/helpers/Constants';
import { ProductionStatusEnum } from 'Common/Constants/Enumeration/ProductionStatusEnum';

import * as EditProductionSelectors from './selectors';
import * as types from './types';

export const setStartFromIndex = (index) => ({
  type: types.EDIT_PRODUCTION_SET_START_FROM_INDEX,
  payload: index,
});

export const changeStartFromIndex = (index) => (dispatch, getState) => {
  const state = getState();
  const prodRecords = EditProductionSelectors.getProdRecords(state);
  let startFromIndex = index;

  const numberOfRecords =
    (Array.isArray(prodRecords) && prodRecords.length) || 0;

  if (!numberOfRecords) {
    startFromIndex = 0;
  } else if (startFromIndex < 0) {
    startFromIndex = numberOfRecords > 0 ? numberOfRecords - 1 : 0;
  }

  dispatch(setStartFromIndex(startFromIndex));
};

const loadProductionRecordWithDetails = async (MPWWebApi, prodRecord) => {
  try {
    const response = await MPWWebApi.postGenericInsertUpdate(
      'Production/GetProductionRecord',
      {
        [ProdRecordFields.SiteId]: prodRecord[ProdRecordFields.SiteId],
        [ProdRecordFields.ServingDT]: prodRecord[ProdRecordFields.ServingDT],
        [ProdRecordFields.CycleMenuId]:
          prodRecord[ProdRecordFields.CycleMenuId],
      }
    );

    const prodRecordWithDetails = _.get(response, ['data', 'Data'], []);

    prodRecordWithDetails[ProdRecordFields.IsWaterAvailable] = Boolean(
      prodRecordWithDetails[ProdRecordFields.IsWaterAvailable]
    );
    return addUIFieldsToDetails(prodRecordWithDetails);
  } catch (ex) {
    return null;
  }
};

export const setViewProdRecordIndex = (index) => ({
  type: types.EDIT_PRODUCTION_SET_VIEW_PROD_RECORD_INDEX,
  payload: index,
});

export const viewProdRecordAndLoad =
  (index, shouldLoad = false) =>
  async (dispatch, getState, MPWWebApi) => {
    const state = getState();
    const prodRecords = [...EditProductionSelectors.getProdRecords(state)];
    const startFromIndex = EditProductionSelectors.getStartFromIndex(state);
    const loadedRecordsMap = {
      ...EditProductionSelectors.getLoadedRecordsMap(state),
    };

    let prodRecord = prodRecords[index];
    dispatch(setViewProdRecordIndex(index));

    if (prodRecord) {
      // Load the production record recipes if we don't have them yet
      if (!loadedRecordsMap[index] || shouldLoad) {
        try {
          dispatch(
            loading(types.EDIT_PRODUCTION_FETCHING_PRODUCTION_RECORDS, true)
          );

          const loadedProdRecord = await loadProductionRecordWithDetails(
            MPWWebApi,
            prodRecord
          );
          if (loadedProdRecord) {
            prodRecords[index] = loadedProdRecord;
            loadedRecordsMap[index] = true;
          }
          dispatch(initializeForm(prodRecords, startFromIndex, index));
          dispatch(updateTemperaturesInUse());
          dispatch(setLoadedRecordsMap(loadedRecordsMap));
        } catch (ex) {
          dispatch(
            handleErrorException(ex, types.EDIT_PRODUCTION_SET_ERROR, {
              Title: 'Error',
              Message: 'Failed to load production record recipes.',
            })
          );
        } finally {
          dispatch(
            loading(types.EDIT_PRODUCTION_FETCHING_PRODUCTION_RECORDS, false)
          );
        }
      }
    }
  };

export const initializeForm = (
  prodRecords,
  startFromIndex,
  viewProdRecordIndex
) => ({
  type: types.EDIT_PRODUCTION_FORM_INITIALIZE,
  payload: {
    prodRecords,
    startFromIndex,
    viewProdRecordIndex,
  },
});

export const getProdRecords =
  (data) => async (dispatch, getState, MPWWebApi) => {
    try {
      const state = getState();
      dispatch(
        loading(types.EDIT_PRODUCTION_FETCHING_PRODUCTION_RECORDS, true)
      );
      const serveDate = data[ProdRecordFields.ServingDT];
      const values = _.omit(data, ProdRecordFields.ServingDT);
      const loadedRecordsMap = {
        ...EditProductionSelectors.getLoadedRecordsMap(state),
      };
      let response = await MPWWebApi.postGenericInsertUpdate(
        'Production/GetProductionRecords',
        values
      );

      const prodRecords = response.data.Data;
      let startFromIndex = 0;
      let viewProdRecordIndex = 0;

      // Load the production record with the specified date
      if (serveDate) {
        const index = prodRecords.findIndex((record) =>
          moment(serveDate).isSame(record[ProdRecordFields.Date], 'day')
        );

        if (index > -1) {
          // Load production record with recipes

          const loadedProdRecord = await loadProductionRecordWithDetails(
            MPWWebApi,
            prodRecords[index]
          );
          if (loadedProdRecord) {
            prodRecords[index] = loadedProdRecord;
            loadedRecordsMap[index] = true;
          }
          // Avoid displaying dates in wrong order when there aren't any dates to cycle through
          if (index >= NUMBER_OF_DAYS_TO_DISPLAY) {
            startFromIndex = index;
          }
          viewProdRecordIndex = index;
        }
      } else {
        // Load the recipes for the first record
        if (prodRecords.length > 0) {
          const loadedProdRecord = await loadProductionRecordWithDetails(
            MPWWebApi,
            prodRecords[0]
          );
          if (loadedProdRecord) {
            prodRecords[0] = loadedProdRecord;
            loadedRecordsMap[0] = true;
          }
        }
      }

      dispatch(
        initializeForm(prodRecords, startFromIndex, viewProdRecordIndex)
      );
      dispatch(setLoadedRecordsMap(loadedRecordsMap));
      dispatch(initializeTemperatures(prodRecords));
    } catch (ex) {
      dispatch(
        handleErrorException(ex, types.EDIT_PRODUCTION_SET_ERROR, {
          Title: 'Error',
          Message: 'Failed to load production records.',
        })
      );
    } finally {
      dispatch(
        loading(types.EDIT_PRODUCTION_FETCHING_PRODUCTION_RECORDS, false)
      );
    }
  };

export const clearError = () => {
  return {
    type: types.EDIT_PRODUCTION_CLEAR_ERROR,
  };
};

export const saveProdRecords =
  ({ data, startDate, endDate }) =>
  async (dispatch, getState, MPWWebApi) => {
    try {
      const state = getState();
      const currentIndex =
        EditProductionSelectors.getViewProdRecordIndex(state);
      const initialProdRecords =
        EditProductionSelectors.getInitialProdRecords(state);
      const startFromIndex = EditProductionSelectors.getStartFromIndex(state);
      const productionRecords = data;

      try {
        dispatch(
          loading(types.EDIT_PRODUCTION_FETCHING_PRODUCTION_RECORDS, true)
        );
        for (const i in productionRecords) {
          const pr = productionRecords[i];
          if (
            currentIndex === Number(i) ||
            !_.isEqual(pr, initialProdRecords[i])
          ) {
            const response = await MPWWebApi.postGenericInsertUpdate(
              'Production/Write',
              convertProdRecordForSave(pr)
            );
            productionRecords[i] = addUIFieldsToDetails(response.data.Data);
          }
        }
        dispatch(
          initializeForm(productionRecords, startFromIndex, currentIndex)
        );
        dispatch(
          success(
            types.EDIT_PRODUCTION_SET_SAVE_PRODUCTION_RECORDS_SUCCESS,
            true
          )
        );
        dispatch(
          loading(types.EDIT_PRODUCTION_FETCHING_PRODUCTION_RECORDS, false)
        );
        dispatch(getRecipeLeftovers(startDate, endDate));
      } catch (exceptions) {
        dispatch(
          handleErrorException(exceptions, types.EDIT_PRODUCTION_SET_ERROR, {
            Title: 'Error',
            Message: 'Failed to save production record.',
          })
        );
      } finally {
        dispatch(
          loading(types.EDIT_PRODUCTION_FETCHING_PRODUCTION_RECORDS, false)
        );
      }
    } catch (ex) {
      dispatch(
        handleErrorException(ex, types.EDIT_PRODUCTION_SET_ERROR, {
          Title: 'Error',
          Message: 'Failed to save production record.',
        })
      );
    }
  };

export const closeProdRecord =
  (data, index) => async (dispatch, getState, MPWWebApi) => {
    try {
      const state = getState();
      const startFromIndex = EditProductionSelectors.getStartFromIndex(state);
      dispatch(
        loading(types.EDIT_PRODUCTION_CLOSE_PRODUCTION_RECORD_LOADING, true)
      );

      const saveResponse = await MPWWebApi.postGenericInsertUpdate(
        'Production/Write',
        data
      );

      const savedRecord = saveResponse.data.Data;
      const id = savedRecord[ProdRecordFields.Id];

      const closeResponse = await MPWWebApi.postGenericInsertUpdate(
        `Production/Close/${id}`
      );

      const closedRecord = closeResponse.data.Data;

      const record = {
        ...savedRecord,
        [ProdRecordFields.CloserUserId]:
          closedRecord[ProdRecordFields.CloserUserId],
        [ProdRecordFields.IsClosed]: closedRecord[ProdRecordFields.IsClosed],
        [ProdRecordFields.Status]: ProductionStatusEnum.Closed,
      };

      const recordWithUpdatedDetails = addUIFieldsToDetails(record);

      dispatch(
        success(types.EDIT_PRODUCTION_CLOSE_PRODUCTION_RECORD_SUCCESS, true)
      );

      const prodRecords = [...EditProductionSelectors.getProdRecords(state)];
      prodRecords[index] = recordWithUpdatedDetails;

      dispatch(initializeForm(prodRecords, startFromIndex, index));
    } catch (ex) {
      dispatch(
        handleErrorException(
          ex,
          types.EDIT_PRODUCTION_CLOSE_PRODUCTION_RECORD_ERROR,
          {
            Title: 'Error',
            Message: 'Failed to close production record.',
          }
        )
      );
    } finally {
      dispatch(
        loading(types.EDIT_PRODUCTION_CLOSE_PRODUCTION_RECORD_LOADING, false)
      );
    }
  };

export const setSaveSuccessful = (saveSuccessful) => ({
  type: types.EDIT_PRODUCTION_SET_SAVE_PRODUCTION_RECORDS_SUCCESS,
  payload: saveSuccessful,
});

export const setCloseSuccessful = (successful) => ({
  type: types.EDIT_PRODUCTION_CLOSE_PRODUCTION_RECORD_SUCCESS,
  payload: successful,
});

export const setStartOverSuccessful = (successful) => ({
  type: types.EDIT_PRODUCTION_START_OVER_SUCCESS,
  payload: successful,
});

export const initializeTemperatures = (prodRecords) => (dispatch, getState) => {
  const temperaturesInUse = getTemperaturesInUse(prodRecords);
  const temperatures = getTemperaturesList(temperaturesInUse);

  dispatch({
    type: types.EDIT_PRODUCTION_SET_TEMPERATURES,
    payload: temperatures,
  });
};

export const updateTemperaturesInUse = () => (dispatch, getState) => {
  const state = getState();
  const prodRecords = EditProductionSelectors.getProdRecords(state);
  const temperaturesInUse = getTemperaturesInUse(prodRecords);
  const currentTemperatures = EditProductionSelectors.getTemperatures(state);
  const temperatures = getTemperaturesList(temperaturesInUse);

  const temperaturesToAdd = temperatures.filter(
    (t) => Boolean(t) && currentTemperatures.indexOf(t) === -1
  );

  const newTemperatures = [...currentTemperatures];

  newTemperatures.forEach((t, i) => {
    if (!newTemperatures[i]) {
      const [temperature] = temperaturesToAdd.splice(0);

      if (temperature) {
        newTemperatures[i] = temperature;
      }
    }
  });

  dispatch({
    type: types.EDIT_PRODUCTION_SET_TEMPERATURES,
    payload: newTemperatures.concat(temperaturesToAdd),
  });
};

export const addTemperature = () => (dispatch, getState) => {
  const state = getState();
  const temperatures = EditProductionSelectors.getTemperatures(state);

  dispatch({
    type: types.EDIT_PRODUCTION_SET_TEMPERATURES,
    payload: [...temperatures, ''],
  });
};

export const changeTemperature = (i, temperature) => (dispatch, getState) => {
  const state = getState();
  const temperatures = [...EditProductionSelectors.getTemperatures(state)];

  const newTemperatures = [...temperatures];
  newTemperatures.splice(i, 1, temperature);

  dispatch({
    type: types.EDIT_PRODUCTION_SET_TEMPERATURES,
    payload: newTemperatures,
  });
};

export const clearState = () => ({
  type: types.EDIT_PRODUCTION_CLEAR_STATE,
});

export const setLoadedRecordsMap = (loadedRecordsMap) => ({
  type: types.EDIT_PRODUCTION_SET_LOADED_RECORDS_MAP,
  payload: loadedRecordsMap,
});

export const setExpandedRecipesByProdRecord = (
  expandedRecipesByProdRecord
) => ({
  type: types.EDIT_PRODUCTION_SET_EXPANDED_RECIPES_BY_PROD_RECORD,
  payload: expandedRecipesByProdRecord,
});

export const toggleShowIngredients =
  (recipeId, isLeftover) => (dispatch, getState) => {
    const state = getState();
    const viewProdRecordIndex =
      EditProductionSelectors.getViewProdRecordIndex(state);
    const expandedRecipesByProdRecord =
      EditProductionSelectors.getExpandedRecipesByProdRecordCopy(state);

    const toggleId = createToggleId(recipeId, isLeftover);

    if (!expandedRecipesByProdRecord[viewProdRecordIndex]) {
      _.setWith(
        expandedRecipesByProdRecord,
        [viewProdRecordIndex, toggleId],
        true,
        Object
      );
    } else {
      const expandedIngMap = {
        ...expandedRecipesByProdRecord[viewProdRecordIndex],
      };
      expandedIngMap[toggleId] = !expandedIngMap[toggleId];

      expandedRecipesByProdRecord[viewProdRecordIndex] = expandedIngMap;
    }

    dispatch(setExpandedRecipesByProdRecord(expandedRecipesByProdRecord));
  };

export const startOverProdRecord =
  (id, index) => async (dispatch, getState, MPWWebApi) => {
    try {
      dispatch(loading(types.EDIT_PRODUCTION_START_OVER_LOADING, true));

      await MPWWebApi.postGenericInsertUpdate(`Production/StartOver/${id}`);
      await dispatch(viewProdRecordAndLoad(index, true));
      dispatch(setStartOverSuccessful(true));
    } catch (ex) {
      dispatch(
        handleErrorException(ex, types.EDIT_PRODUCTION_START_OVER_ERROR, {
          Title: 'Error',
          Message: 'Failed to start over production record.',
        })
      );
    } finally {
      dispatch(loading(types.EDIT_PRODUCTION_START_OVER_LOADING, false));
    }
  };

export const getRecipeLeftovers =
  (startDate, endDate) => async (dispatch, getState, MPWWebApi) => {
    try {
      const state = getState();

      const siteId = EditProductionSelectors.getSiteId(state);

      dispatch(loading(types.EDIT_PRODUCTION_RECIPE_LEFTOVERS_LOADING), true);
      if (siteId && startDate && endDate) {
        const res = await MPWWebApi.postGenericInsertUpdate(
          'Production/GetRecipeLeftovers',
          {
            [ProdRecipeLeftoverFields.SiteId]: siteId,
            [ProdRecipeLeftoverFields.StartDate]: startDate,
            [ProdRecipeLeftoverFields.EndDate]: endDate,
          }
        );

        dispatch(
          success(types.EDIT_PRODUCTION_SET_RECIPE_LEFTOVERS, res.data.Data)
        );
      }
    } catch (ex) {
      dispatch(
        handleErrorException(ex, types.EDIT_PRODUCTION_RECIPE_LEFTOVERS_ERROR, {
          Title: 'Error',
          Message: 'Failed to load leftover recipes.',
        })
      );
    } finally {
      dispatch(loading(types.EDIT_PRODUCTION_RECIPE_LEFTOVERS_LOADING), false);
    }
  };

export const setRecipeLeftovers = (leftovers) => ({
  type: types.EDIT_PRODUCTION_SET_RECIPE_LEFTOVERS,
  payload: leftovers,
});

export const removeAddedLeftovers =
  (leftoverRecipe) => (dispatch, getState) => {
    const state = getState();
    const recipeLeftovers = [
      ...EditProductionSelectors.getRecipeLeftovers(state),
    ];

    const recipeLeftoverIds = leftoverRecipe[
      ProdRecipeLeftoverFields.Leftovers
    ].map((p) => p[ProdRecipeLeftoverFields.RecipeLeftoverId]);

    recipeLeftoverIds.forEach((id) => {
      const leftover = recipeLeftovers.find(
        (rl) => rl[ProdRecipeLeftoverFields.RecipeLeftoverId] === id
      );

      if (leftover) {
        leftover[ProdRecipeLeftoverFields.Portions] = 0;
      }
    });

    dispatch(setRecipeLeftovers(recipeLeftovers));
  };

export const removeLeftover = (leftoverDetail) => (dispatch, getState) => {
  const state = getState();
  const recipeLeftovers = [
    ...EditProductionSelectors.getRecipeLeftovers(state),
  ];

  const leftovers = leftoverDetail[ProdRecordDetailFields.Leftovers];

  leftovers.forEach((l) => {
    const recipeLeftoverId = l[ProdRecipeLeftoverFields.RecipeLeftoverId];

    const index = recipeLeftovers.findIndex(
      (rl) => rl[ProdRecipeLeftoverFields.RecipeLeftoverId] === recipeLeftoverId
    );
    const recipeLeftover = recipeLeftovers[index];

    if (recipeLeftover) {
      recipeLeftover[ProdRecipeLeftoverFields.Portions] +=
        Number(l[ProdRecipeLeftoverFields.Portions]) || 0;
    }
  });

  dispatch(setRecipeLeftovers(recipeLeftovers));
};

export const changeModalVisiblity = (modal, show) => ({
  type: types.EDIT_PRODUCTION_CHANGE_MODAL_VISIBILITY,
  payload: { modal, show },
});

export const updateStoreLeftovers =
  (detail, fieldName, newValue) => (dispatch, getState) => {
    const state = getState();
    const recipeLeftovers = [
      ...EditProductionSelectors.getRecipeLeftovers(state),
    ];
    const leftoversList =
      EditProductionSelectors.getCondensedLeftoversList(state);
    const leftoverRecipe = leftoversList.find(
      (l) =>
        l[ProdRecipeLeftoverFields.Id] ===
        detail[ProdRecordDetailFields.RecipeId]
    ) || {
      [ProdRecipeLeftoverFields.Leftovers]: [],
    };
    let parsedValue = Number(newValue) || 0;

    let newLeftoverAmount = getNewLeftoverAmount({
      detail,
      fieldName,
      value: parsedValue,
    });
    const maxLeftoverAmount = getMaxAllowedForLeftover(detail, leftoverRecipe);

    // If new input causes it to exceed the total available leftovers, adjust it to
    // use the remaining amount
    if (newLeftoverAmount >= maxLeftoverAmount) {
      newLeftoverAmount = maxLeftoverAmount;
    }

    const leftoverDetails = _.cloneDeep(
      detail[ProdRecordDetailFields.Leftovers]
    );

    const leftoverChanges = getLeftoverChanges({
      amount: newLeftoverAmount,
      leftoverDetail: detail,
      leftoverRecipe,
    });

    const { detailChanges, leftoverRecipeChanges } = leftoverChanges;

    detailChanges.forEach((dc) => {
      const leftoverDetail = leftoverDetails.find(
        (ld) =>
          ld[ProdLeftoverDetailFields.RecipeLeftoverId] ===
          dc[ProdLeftoverDetailFields.RecipeLeftoverId]
      );

      // Leftover detail doesn't exist so create one
      if (!leftoverDetail && dc[ProdLeftoverDetailFields.Portions] > 0) {
        leftoverDetails.push(dc);
      } else {
        leftoverDetail[ProdLeftoverDetailFields.Portions] +=
          dc[ProdLeftoverDetailFields.Portions];
      }
    });

    leftoverRecipeChanges.forEach((lc) => {
      const leftoverRecipe = recipeLeftovers.find(
        (rl) =>
          rl[ProdRecipeLeftoverFields.RecipeLeftoverId] ===
          lc[ProdLeftoverDetailFields.RecipeLeftoverId]
      );

      if (leftoverRecipe) {
        leftoverRecipe[ProdLeftoverDetailFields.Portions] +=
          lc[ProdLeftoverDetailFields.Portions];
      }
    });

    detail[ProdRecordDetailFields.Leftovers] = leftoverDetails.filter(
      (ld) => ld[ProdLeftoverDetailFields.Portions] > 0
    );
    dispatch(setRecipeLeftovers(recipeLeftovers));

    return newLeftoverAmount;
  };

export const updatePlanProductionValues = (prodRecords) => {
  return {
    type: types.EDIT_PRODUCTION_UPDATE_PRODUCTION_PLAN,
    payload: prodRecords,
  };
};



// WEBPACK FOOTER //
// ./src/pages/manageproduction/state/editproduction/actions.js